<template>
  <div class="attendee-list">
    <!-- Invited and not in yet, you still come first. -->
    <div v-if="myInvite" class="row invitee">
      <GamInviteeRow class="attendee" :invitee="myInvite" :order="0" />
    </div>

    <div class="row" v-for="(attendee, a) in sortedAttendees" :key="`attendee-${a}`">
      <GamAttendeeRow
        class="attendee"
        :player="attendee"
        :chess-set-count="attendee?.chessSetCount || 0"
        :is-organizer="attendee?.id === gambit?.ownerId || attendee?.isOrganizer"
        :is-handshake="canCreateHandshakeWith(attendee) || canReturnHandshakeTo(attendee)"
        :show-check-in="
          activeCheckIns?.some((c) => c.userId === attendee.id && (c.end === null || new Date(c.end) > new Date()))
        "
        :show-coach="isLesson"
        :show-streamer="isStream"
        show-unpaired-badge
        :order="a"
        :is-row-action="canCreateHandshakeWith(attendee) || canReturnHandshakeTo(attendee)"
        @select="handleHandshakeClick(attendee)"
      />
      <template v-if="canCreateHandshakeWith(attendee) || canReturnHandshakeTo(attendee)">
        <HandshakeButton
          class="handshake-button"
          :cta-label="ctaLabelFor(attendee.id)"
          @click="handleHandshakeClick(attendee)"
          :is-initiated="!!getPendingHandshakeFor(attendee.id)"
          :is-pending="getPendingHandshakeFor(attendee.id)?.isConfirmed === false"
          :is-completed="showsFilledHandshake(attendee.id)"
          :is-recommendation="liveTap.cta(attendee.id) === 'open'"
          :is-unplayed="liveTap.isUnplayed(attendee.id)"
          :position="liveTap.recommendation(attendee.id).position"
          :position-on="liveTap.recommendation(attendee.id).state === 'open'"
          :responder-id="getHandshakeFor(attendee.id)?.responderId || null"
        />
      </template>
    </div>

    <div class="row invitee" v-for="(invitee, i) in otherInvitees" :key="`invitee-${i}`">
      <GamInviteeRow v-if="invitee" class="attendee" :invitee="invitee" :order="i" />
    </div>

    <div v-if="canInvite" class="row invite-row">
      <div class="seat-ring is-invite" @click="listEmit(GamComponentsEmits.INVITE)">
        <GamIcon :name="GamIconName.PLUS" size="24px" is-current />
      </div>
    </div>

    <GamConfirmModal
      v-if="cancelHandshakeId"
      :title="localize('handshake.cancel.title')"
      :keep-label="localize('handshake.cancel.dismiss')"
      :destructive-label="localize('handshake.cancel.confirm')"
      @close="cancelHandshakeId = null"
      @keep="cancelHandshakeId = null"
      @confirm="handleConfirmCancelHandshake"
    />
  </div>
</template>

<script lang="ts">
export interface AttendeeCheckInDto {
  userId: string;
  start: string;
  end: string | null;
  locationId: string | null;
  gambitId: string | null;
}

interface AttendeeListGambitDto {
  id: string;
  start: string;
  end: string;
  ownerId: string | null;
}

interface AttendeeListLocationDto {
  id: string;
  name: string;
  handshakes?: HandshakeDto[];
  pendingHandshakes?: HandshakeDto[];
}

export interface CheckInDto {
  userId: string;
  start: string;
  end: string | null;
}

export interface AttendeeListProps {
  gambit?: AttendeeListGambitDto;
  location?: AttendeeListLocationDto;

  attendees?: Array<AttendeeDto>;
  invitees?: Array<InviteeDto>;

  user?: { id: string } | null;

  activeCheckIns: CheckInDto[];
  handshakeEligibleCheckIns?: string[];
  recentCheckIns?: string[];

  handshakes: HandshakeDto[];
  pendingHandshakes: HandshakeDto[];

  isLesson?: boolean;
  isStream?: boolean;

  canInvite?: boolean;
}
</script>

<script setup lang="ts">
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { AttendeeDto, InviteeDto } from '@/core/data/user/user.interface';
import { gambits, localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import GamAttendeeRow from '@/views/components/gam-player/GamAttendeeRow.vue';
import GamInviteeRow from '@/views/components/gam-player/GamInviteeRow.vue';
import GamConfirmModal from '@/views/components/GamConfirmModal.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { useConfetti } from '@/views/composables/hooks/useConfetti';
import { useCtaLabels } from '@/views/composables/hooks/useCtaLabels';
import { useLiveHandshakeTap } from '@/views/composables/hooks/useLiveHandshakeTap';
import {
  isHandshakeModalOpen,
  markHandshakeModalClosed,
  markHandshakeModalOpen,
  markHandshakeShown,
  wasHandshakeShown,
} from '@/views/main/handshakes/handshake-modal-queue';
import HandshakeButton from '@/views/main/handshakes/HandshakeButton.vue';
import HandshakeModal from '@/views/main/handshakes/HandshakeModal.vue';
import { celebrateNewlyConfirmedHandshakes } from '@/views/main/handshakes/utils/handshake-celebration';
import { hasSpentGambitHandshake, isHandshakeLive } from '@/views/main/handshakes/utils/handshake-game';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useModal } from 'vue-final-modal';
import { useToast } from 'vue-toastification';

const props = defineProps<AttendeeListProps>();
const listEmit = defineEmits([GamComponentsEmits.UPDATE, GamComponentsEmits.INVITE]);

const authStore = useAuthStore();
const { user: authUser } = storeToRefs(authStore);

/**
 * You first, so every row under yours has its handshake in the same place, then
 * whoever runs the gambit, then everyone checked in. Otherwise the order they joined.
 */
const sortedAttendees = computed(() => {
  const me = authUser.value?.id;
  const rank = (attendee: AttendeeDto): number => {
    if (me && attendee.id === me) return 0;
    if (attendee.id === props.gambit?.ownerId || attendee.isOrganizer) return 1;
    return attendee.isCheckedIn ? 2 : 3;
  };

  return [...(props.attendees || [])].sort((a, b) => rank(a) - rank(b));
});

const myInvite = computed(() => {
  const me = authUser.value?.id;
  return (me && props.invitees?.find((invitee) => invitee?.id === me)) || null;
});

const otherInvitees = computed(() => (props.invitees || []).filter((invitee) => invitee !== myInvite.value));

const isHandshakeEligible = computed(() => {
  if (!props.user?.id) return false;
  return props.handshakeEligibleCheckIns?.includes(props.user.id) ?? false;
});

const getHandshakeFor = (playerId: string) => {
  const playerHandshakes = [...(props.handshakes || []), ...(props.pendingHandshakes || [])];
  const now = new Date();

  const filteredHandshakes = playerHandshakes
    .filter(
      (h) =>
        isHandshakeLive(h.createdAt, now) &&
        ((h.initiatorId === authUser.value?.id && h.responderId === playerId) ||
          (h.initiatorId === playerId && h.responderId === authUser.value?.id)),
    )
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  return filteredHandshakes[0] || null;
};

const getPendingHandshakeFor = (playerId: string) => {
  const playerHandshakes = props.pendingHandshakes
    .filter(
      (h) =>
        (h.initiatorId === authUser.value?.id && h.responderId === playerId) ||
        (h.initiatorId === playerId && h.responderId === authUser.value?.id),
    )
    .sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

  return playerHandshakes[0] || null;
};

const toast = useToast();
const liveTap = useLiveHandshakeTap(() => ({ locationId: props.location?.id, gambitId: props.gambit?.id }));

const handshakeAttendee = ref<{ id: string; username: string; profileImageBlobHash: string | null } | null>(null);
const currentHandshake = ref<HandshakeDto | null>(null);
const currentGambitId = ref<string | null>(props.gambit?.id || null);
const currentLocationId = ref<string | null>(props.location?.id || null);
const showTimestamp = ref<boolean>(true);
const nowDate = ref<Date>(new Date());
const alreadyShakenHere = (playerId: string) =>
  hasSpentGambitHandshake(props.handshakes || [], props.gambit?.id, authUser.value?.id, playerId, nowDate.value);
const showsFilledHandshake = (playerId: string) => alreadyShakenHere(playerId);

const { isLearning } = useCtaLabels();

/** What the button does, in words, while the player is still new to it. */
const ctaLabelFor = (playerId: string): string | undefined => {
  if (!isLearning.value || liveTap.cta(playerId) === 'open') return undefined;

  const pending = getPendingHandshakeFor(playerId);
  if (pending?.initiatorId === authUser.value?.id || showsFilledHandshake(playerId)) return undefined;

  return localize('live.banner.handshake');
};

const timeInterval = ref<number>();
onMounted(() => {
  if (timeInterval.value) {
    clearInterval(timeInterval.value);
  }

  timeInterval.value = setInterval(() => {
    nowDate.value = new Date();
  }, 60 * 1000) as any as number;
});

onUnmounted(() => {
  if (timeInterval.value) {
    clearInterval(timeInterval.value);
  }
});

const isModalOpen = ref(false);

const setModalOpen = (open: boolean) => {
  if (isModalOpen.value === open) return;
  isModalOpen.value = open;
  if (open) markHandshakeModalOpen();
  else markHandshakeModalClosed();
};

onUnmounted(() => setModalOpen(false));

const { open: openHandshakeModal, close } = useModal({
  component: HandshakeModal,
  attrs: {
    player: handshakeAttendee,
    handshake: currentHandshake,
    gambitId: currentGambitId,
    locationId: currentLocationId,
    showTimestamp: showTimestamp,
    onClose: (options?: { refresh?: boolean }) => {
      setModalOpen(false);
      if (currentHandshake.value) {
        markHandshakeShown(currentHandshake.value.id);
      }
      currentHandshake.value = null;
      close();
      if (options?.refresh) {
        listEmit(GamComponentsEmits.UPDATE);
      }
    },
  },
});

const cancelHandshakeId = ref<string | null>(null);

const handleHandshakeClick = async (attendee: AttendeeDto) => {
  const pending = getPendingHandshakeFor(attendee.id);
  if (pending && pending.initiatorId === authUser.value?.id) {
    cancelHandshakeId.value = pending.id;
    return;
  }

  if (await liveTap.tap(attendee.id)) return;

  if (alreadyShakenHere(attendee.id)) {
    toast.info(localize('handshake.game.already'));
    return;
  }

  handshakeAttendee.value = attendee;
  currentHandshake.value = getHandshakeFor(attendee.id) || getPendingHandshakeFor(attendee.id) || null;
  showTimestamp.value = !!getHandshakeFor(attendee.id);

  setModalOpen(true);
  nextTick(() => {
    openHandshakeModal();
  });
};

const handleConfirmCancelHandshake = async () => {
  if (!cancelHandshakeId.value) return;
  await gambits.userService.deleteHandshake(cancelHandshakeId.value);
  cancelHandshakeId.value = null;
  listEmit(GamComponentsEmits.UPDATE);
};

watch(
  () => props.pendingHandshakes,
  (newPendingHandshakes) => {
    if (!authUser.value) return;

    if (currentHandshake.value && !currentHandshake.value.isConfirmed && isModalOpen.value) {
      const allHandshakes = [...(props.handshakes || []), ...(newPendingHandshakes || [])];
      if (!allHandshakes.some((h) => h.id === currentHandshake.value!.id)) {
        setModalOpen(false);
        currentHandshake.value = null;
        close();
        listEmit(GamComponentsEmits.UPDATE);
        return;
      }
    }

    if (isHandshakeModalOpen()) return;
    if (!newPendingHandshakes?.length) return;

    const incomingHandshake = newPendingHandshakes.find(
      (h) => !h.isConfirmed && h.responderId === authUser.value!.id && !wasHandshakeShown(h.id),
    );

    if (!incomingHandshake?.initiator) return;

    markHandshakeShown(incomingHandshake.id);
    handshakeAttendee.value = incomingHandshake.initiator;
    currentHandshake.value = incomingHandshake;

    setModalOpen(true);
    nextTick(() => {
      openHandshakeModal();
    });
  },
  { immediate: true },
);

const { fire: fireConfetti } = useConfetti();

watch(
  () => props.handshakes,
  (next, previous) => {
    celebrateNewlyConfirmedHandshakes(previous, next, authUser.value?.id, fireConfetti);
  },
);

function isOtherPlayer(id: string) {
  return id !== authUser.value?.id;
}

function canCreateHandshakeWith(player: { id: string }) {
  if (!isHandshakeEligible.value) return false;
  if (!isOtherPlayer(player.id)) return false;

  return props.handshakeEligibleCheckIns?.includes(player.id) ?? false;
}

function canReturnHandshakeTo(player: { id: string }) {
  if (!isOtherPlayer(player.id)) return false;

  const handshake = getPendingHandshakeFor(player.id);
  if (!handshake) return false;

  return !handshake.isConfirmed;
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.attendee-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 0.5rem;
  gap: var(--spacing-md);
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs);
}

.invitee {
  opacity: 0.33;
  pointer-events: none;
}

.invite-row {
  justify-content: flex-end;
}

.seat-ring {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-white-20);
  color: var(--color-white-40);
}

.seat-ring.is-invite {
  border-style: dashed;
  border-color: var(--color-white-30);
  color: var(--color-white-60);
  cursor: pointer;
  transition: color 0.15s ease;
}
@include hover-capable {
  .seat-ring.is-invite:hover {
    color: var(--color-white);
  }
}

/* Flat, so the raised button beside it is the one thing that reads as the action. */
.attendee {
  flex-shrink: 1;
}

.spacer {
  width: 56px;
  height: 52px;
  flex-shrink: 0;
}
</style>
